// THE SITE BUILDER — the shell's /preview as a builder (Nika 2026-10-28:
// "website preview mode AI is broken. It should work like lovable").
//
// One chat (the shell assistant, docked beside the builder), one frame that
// shows the LATEST SAVED version of the site, and a frame that follows the
// assistant's project writes DURING the turn. Every function here is
// registered on the shell's EAGER registry (functions/eagerSlice.js): the
// shell transport resolves the site on screen per send and hands every tool
// frame to `previewAfterAiWrite`, before any /preview fold.
//
// Root keys (declared + scoped in workspace state-app.js, "Site builder"):
//   _previewSite        the builder's record `{ owner, key, projectId, env, page }`
//   _previewSiteStatus  null | 'setup' | 'updating'
//   _previewSiteStep    the running project write's label, or null
//   _previewSiteDraft   the version on screen is not the published one
//   _aiProjectWrite     the last AI project write `{ projectId, tool, at }`

// The builder on screen: a PageSlot pane showing /preview (primary pane
// first), else the live route when it is /preview. `{ owner, key, env }`
// from that surface's own address, the Studio carrier (`_previewParams`)
// filling a builder that names no site; `{}` for a builder that shows no
// site yet (a new site being set up). null when no builder is on screen —
// a closed pane or another route is never the site on screen, whatever a
// kept-alive builder last recorded.
export function previewBuilderOnScreen(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : (el && el.state && el.state.root) || null
  if (!r) return null
  const clean = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')
  const pathOf = (href) => {
    const path = String(href || '')
      .split('#')[0]
      .split('?')[0]
    let out = null
    try {
      out = el && typeof el.call === 'function' ? el.call('stripWorkspacePrefix', path) : null
    } catch (_) {
      out = null
    }
    return typeof out === 'string' ? out : path.replace(/^\/w\/[^/]+(?=\/|$)/, '')
  }
  const isBuilder = (href) => {
    const p = pathOf(href)
    return p === '/preview' || p.indexOf('/preview/') === 0
  }
  const queryOf = (href) => {
    const raw = String(href || '').split('#')[0]
    const at = raw.indexOf('?')
    return new URLSearchParams(at === -1 ? '' : raw.slice(at + 1))
  }
  let q = null
  for (const paneKey of ['_canvasSlot', '_canvasSlotSecond']) {
    const href = r[paneKey]
    if (typeof href === 'string' && href && isBuilder(href)) {
      q = queryOf(href)
      break
    }
  }
  if (!q) {
    let route = ''
    try {
      route = el && typeof el.call === 'function' ? el.call('readPageRoute') : ''
    } catch (_) {
      route = ''
    }
    if (typeof route !== 'string' || !route) route = r.currentPath || ''
    if (!isBuilder(route)) return null
    let params = null
    try {
      params = el.call('readPageParams')
    } catch (_) {
      params = null
    }
    q = params instanceof URLSearchParams ? params : new URLSearchParams()
  }
  let owner = clean(q.get('owner'))
  let key = clean(q.get('key')).split('.')[0]
  let env = clean(q.get('env'))
  if (!owner || !key) {
    const p = r._previewParams && typeof r._previewParams === 'object' ? r._previewParams : {}
    owner = clean(p.owner)
    key = clean(String(p.key || '')).split('.')[0]
    env = clean(p.env)
  }
  if (!owner || !key) return {}
  return env ? { owner, key, env } : { owner, key }
}

// THE SITE ON SCREEN, resolved PER SEND (the shell transport's
// `context.previewProject`): `{ owner, key, projectId?, env?, page? }` —
// the builder's own record adds the project id, the version it shows and
// its page, only while it names the SAME site. null off the builder, on a
// builder that shows no site yet, and after the pane closed.
export function previewSiteInView(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : (el && el.state && el.state.root) || null
  if (!r || !el || typeof el.call !== 'function') return null
  const shown = el.call('previewBuilderOnScreen', r)
  if (!shown || !shown.owner || !shown.key) return null
  const ENVS = ['production', 'staging', 'development']
  const site = r._previewSite && typeof r._previewSite === 'object' ? r._previewSite : null
  const same = !!site && site.owner === shown.owner && site.key === shown.key
  const out = { owner: shown.owner, key: shown.key }
  if (same && typeof site.projectId === 'string' && site.projectId) out.projectId = site.projectId
  const env = same ? site.env : shown.env
  if (ENVS.includes(env)) out.env = env
  // The page on screen: the builder's own pick (its page picker or its
  // sidebar, `activePage`), else the page it recorded.
  const page = same ? (typeof r.activePage === 'string' && r.activePage) || site.page : ''
  if (typeof page === 'string' && page.charAt(0) === '/') out.page = page
  return out
}

// The builder's read: the LATEST SAVED version of the site (branch main,
// version latest) — never an environment's published pin, which the shared
// read caches for ten minutes; a `version` pin (Version History "Preview
// this version") wins. `{ data, projectId, draft }`: `draft` is true when
// the version read is not the published one (never published, or a newer
// save), false when unknown — the mark stays quiet rather than wrong.
export async function previewSiteFetch(target) {
  const el = this
  const t = target && typeof target === 'object' ? target : {}
  const clean = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')
  const owner = clean(t.owner)
  const key = clean(String(t.key || '')).split('.')[0]
  const version = clean(t.version)
  if (!owner || !key) throw new Error('Provide both owner and key')
  const fns = (el && el.context && el.context.functions) || {}
  const read =
    typeof fns.getProjectData === 'function'
      ? (opts) => el.call('getProjectData', opts)
      : async (opts) =>
          (await import('@symbo-ls/workspace-shared/functions/fetchProject.js')).getProjectData(
            opts
          )
  // `env: ''`, not null: the shared read falls back to the ADDRESS BAR's
  // env for a nullish one, and a /preview address names the env a Fly card
  // opened (always production) — that pinned the published version.
  const data = await read({
    raw: true,
    owner,
    key,
    env: '',
    branch: 'main',
    version: version || 'latest'
  })
  let projectId = data && (data._id || data.id) ? String(data._id || data.id) : ''
  let draft = false
  if (!version) {
    try {
      const sdk = el.sdk || el.call('getSDK')
      if (sdk && typeof sdk.getProjectByKey === 'function') {
        const res = await sdk.getProjectByKey({ owner, key })
        const p = res && typeof res === 'object' && 'data' in res && res.success ? res.data : res
        if (p && typeof p === 'object') {
          if (!projectId && (p._id || p.id)) projectId = String(p._id || p.id)
          const pv = p.publishedVersion
          const pvValue = pv && typeof pv === 'object' ? clean(String(pv.value || '')) : ''
          const v = data && data.version
          const latest = clean(String((v && typeof v === 'object' ? v.value : v) || ''))
          if (!pv) draft = true
          else if (pvValue && latest) draft = pvValue !== latest
        }
      }
    } catch (_) {
      draft = false
    }
  }
  return { data, projectId: projectId || null, draft }
}

// Show a site in the builder: read its latest saved version and land it on
// root — the data (the Scene frame boots on it), the builder's record, the
// draft mark. `this` is any element of the builder. Returns the data.
export async function previewSiteShow(target) {
  const el = this
  const root = el && el.state && el.state.root
  const t = target && typeof target === 'object' ? target : {}
  if (!root || typeof root.update !== 'function') return null
  const got = await el.call('previewSiteFetch', t)
  const data = got && got.data
  if (!data) return null
  const prev = root._previewSite && typeof root._previewSite === 'object' ? root._previewSite : null
  const same = !!prev && prev.owner === t.owner && prev.key === t.key
  root.update({
    ...el.call('previewPagePatch', data, { owner: t.owner, key: t.key }),
    // A different site opens on its home (a previous site's page would name
    // a page this one does not have).
    ...(same ? {} : { activePage: '/' }),
    _previewSite: {
      owner: t.owner,
      key: String(t.key).split('.')[0],
      projectId: (got && got.projectId) || null,
      env: null,
      page: (same && prev.page) || '/'
    },
    _previewSiteDraft: !!(got && got.draft),
    _previewSiteStatus: null,
    fetchError: null
  })
  return data
}

// Show one of the site's pages in the builder frame: the frame's own port
// (the opaque user-code frame, shared userCodeFrame.js) navigates, and the
// page becomes the one the assistant is told about. `this` is an element
// of the builder — the frame is found down from its page, never through
// the document.
export function previewSiteGoTo(path) {
  const el = this
  const root = el && el.state && el.state.root
  const p = typeof path === 'string' && path.charAt(0) === '/' ? path : ''
  if (!root || typeof root.update !== 'function' || !p) return false
  const site = root._previewSite && typeof root._previewSite === 'object' ? root._previewSite : null
  root.update({ activePage: p, ...(site ? { _previewSite: { ...site, page: p } } : {}) })
  const page =
    typeof el.lookup === 'function' ? el.lookup((e) => !!(e && e.AppPage && e.PreviewNavbar)) : null
  const frame = page && typeof page.lookdown === 'function' ? page.lookdown('AppIframe') : null
  const handle = frame && frame.node ? el.call('getUserCodeHandle', frame.node) : null
  if (handle && typeof handle.post === 'function') handle.post('navigate', { path: p })
  return true
}

// The builder's address follows its site — a new site set up by the
// assistant is pointed at once its project is known. `projectId` alone
// resolves the owner and the key (the server's tool frame names only the
// id). Returns true when the builder now shows that site.
export async function previewSiteAdopt(projectId) {
  const el = this
  const root = el && el.state && el.state.root
  const id = typeof projectId === 'string' ? projectId.trim() : ''
  if (!root || typeof root.update !== 'function' || !id) return false
  const sdk = el.sdk || el.call('getSDK')
  if (!sdk || typeof sdk.getProject !== 'function') return false
  let p = null
  try {
    const res = await sdk.getProject(id)
    p = res && typeof res === 'object' && 'data' in res && res.success ? res.data : res
  } catch (_) {
    p = null
  }
  // The owner's SLUG — an org-owned project names `{ slug }`, a user-owned
  // one `{ username }`; a bare id is no slug (the canvas site cards read the
  // same way, canvas functions/canvasAnswer.js canvasAnswerCards).
  const slugOf = (v) => {
    if (!v) return ''
    if (typeof v === 'string') return /^[a-f0-9]{24}$/i.test(v) ? '' : v
    return v.slug || v.username || ''
  }
  const claims = root.jwtClaims || {}
  const orgs = Array.isArray(claims.orgs) ? claims.orgs : []
  const org = orgs.find((x) => x && x.id === claims.workspace_org_id)
  const owner =
    slugOf(p && p.owner) ||
    slugOf(p && p.workspace && p.workspace.organization) ||
    (p && typeof p.ownerUsername === 'string' ? p.ownerUsername : '') ||
    (org && org.slug) ||
    ''
  const key = p && typeof p.key === 'string' ? p.key.split('.')[0] : ''
  if (!owner || !key) return false
  root.update({ _previewParams: { owner, key } })
  await el.call('previewSiteShow', { owner, key })
  return true
}

// THE ASSISTANT'S WRITES REACH THE FRAME DURING THE TURN. The shell
// transport hands every tool-activity frame here (functions/
// simoneAiTransport.js onToolEvent). A project write for the site on
// screen shows its own label as the builder's chip while it runs, and the
// frame reloads the latest saved version the moment it lands — not at the
// end of the turn. A builder setting up a new site adopts the first project
// a write names. Every project write the shell sees is recorded for the
// canvas editor (`_aiProjectWrite`). Reads never change anything.
export function previewAfterAiWrite(evt, root) {
  const el = this
  const e = evt && typeof evt === 'object' ? evt : null
  const r =
    root && typeof root.update === 'function' ? root : (el && el.state && el.state.root) || null
  if (!e || !r || !e.tool || !el || typeof el.call !== 'function') return false
  const tool = String(e.tool)
  if (/^(get|list|read|search|find|fetch|describe|count)_/.test(tool)) return false
  // The started frame carries the call's arguments, the completed one only
  // its id: remember them per call, beside the shell's other per-root memos.
  let rootEl = null
  try {
    rootEl = typeof el.getRoot === 'function' ? el.getRoot() : el
  } catch (_) {
    rootEl = el
  }
  const memo = rootEl && rootEl.scope && typeof rootEl.scope === 'object' ? rootEl.scope : null
  if (memo && !(memo.previewSiteCalls instanceof Map)) memo.previewSiteCalls = new Map()
  const calls = memo ? memo.previewSiteCalls : new Map()
  const callId = e.callId ? String(e.callId) : ''
  if (e.phase === 'started' && callId) {
    calls.set(callId, { args: e.args && typeof e.args === 'object' ? e.args : {} })
    while (calls.size > 40) calls.delete(calls.keys().next().value)
  }
  const known = callId ? calls.get(callId) : null
  const args = (known && known.args) || (e.args && typeof e.args === 'object' ? e.args : {})
  const named = typeof args.projectId === 'string' ? args.projectId.trim() : ''
  const builder = el.call('previewBuilderOnScreen', r)
  const site = el.call('previewSiteInView', r)
  const setup = !!builder && !site && r._previewSiteStatus === 'setup'
  const projectWrite =
    tool === 'commit_project_changes' || tool === 'create_project' || tool === 'update_project'
  if (!projectWrite && !named) return false
  // Does this write change the site on screen?
  let forSite = false
  if (tool === 'create_project') forSite = setup
  else if (site) forSite = named ? !site.projectId || named === site.projectId : projectWrite
  else forSite = setup && !!named
  const done = e.phase === 'completed'
  if (done && callId) calls.delete(callId)
  if (done && e.ok !== false && named) {
    r.update({ _aiProjectWrite: { projectId: named, tool, at: Date.now() } })
  }
  if (!forSite) return false
  if (e.phase === 'started') {
    const label = typeof e.label === 'string' && e.label.trim() ? e.label.trim() : null
    r.update({ _previewSiteStep: label || el.call('polyglot', 'preview.builder.working') })
    return true
  }
  if (!done) return false
  // A failed write changed nothing; a new site's create_project names no
  // project yet (the server's frame carries only a summary) — the builder
  // keeps waiting, quietly, for the first write that names it.
  if (e.ok === false || (setup && !named)) {
    r.update({ _previewSiteStep: null })
    return true
  }
  r.update({ _previewSiteStep: null, _previewSiteStatus: 'updating' })
  const settle = () => {
    if (r._previewSiteStatus === 'updating') r.update({ _previewSiteStatus: null })
  }
  // A new site: the first write that names its project points the builder
  // at it (create_project's own frame names no id — the commit after it does).
  const run =
    setup && named
      ? el.call('previewSiteAdopt', named)
      : el.call('reloadPreviewProject', { latest: true })
  Promise.resolve(run)
    .catch((err) => console.warn('[preview] reload after an AI write:', err?.message || err))
    .then(settle)
  return true
}

// The builder leaves: its record, status, chip and draft mark go with it
// (the PageSlot wrapper and the /preview route both call this on removal).
// A record naming ANOTHER site belongs to another builder and stays.
export function previewSiteRelease(target) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return
  const t = target && typeof target === 'object' ? target : null
  const site = root._previewSite
  const mine = !t || !site || (site.owner === t.owner && site.key === t.key)
  if (!mine) return
  root.update({
    _previewSite: null,
    _previewSiteStatus: null,
    _previewSiteStep: null,
    _previewSiteDraft: false
  })
}

// A failed site read as the page's error state (PreviewErrorBoundary): the
// Error flattened — `Error.message` is non-enumerable and the state
// pipeline's clone drops it (same handling as preview/index.js).
export function previewSiteError(err) {
  const e = err || {}
  return {
    code: e.code || 'unknown',
    message: e.message || 'Unknown error',
    visibility: e.visibility || null,
    triedEnvs: Array.isArray(e.triedEnvs) ? e.triedEnvs : [],
    owner: e.owner || null,
    key: e.key || null,
    passwordEnv: e.passwordEnv || null,
    signedIn: !!e.signedIn,
    status: typeof e.status === 'number' ? e.status : null
  }
}

// Can the assistant work here? Only inside the shell — it holds the
// session and the ONE assistant (Nika 2026-10-03). The public preview host
// holds no session (PREVIEW-HOST-PUBLIC-ONLY): its AI toggle could only
// ever answer "sign in".
export function previewAiAvailable() {
  const fns = (this && this.context && this.context.functions) || {}
  return typeof fns.shellSendPrompt === 'function'
}
